import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import Cropper from 'react-easy-crop';
import {
  Dialog, Box, Stack, Button, Slider, Typography, IconButton, Tooltip, Menu, MenuItem,
  ListSubheader, TextField, ToggleButton, ToggleButtonGroup, Divider, CircularProgress, Popover,
} from '@mui/material';
import CropRoundedIcon from '@mui/icons-material/CropRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import PhotoFilterRoundedIcon from '@mui/icons-material/PhotoFilterRounded';
import GestureRoundedIcon from '@mui/icons-material/GestureRounded';
import EmojiEmotionsRoundedIcon from '@mui/icons-material/EmojiEmotionsRounded';
import AddPhotoAlternateRoundedIcon from '@mui/icons-material/AddPhotoAlternateRounded';
import BlurOnRoundedIcon from '@mui/icons-material/BlurOnRounded';
import TitleRoundedIcon from '@mui/icons-material/TitleRounded';
import AspectRatioRoundedIcon from '@mui/icons-material/AspectRatioRounded';
import RotateLeftRoundedIcon from '@mui/icons-material/RotateLeftRounded';
import FlipRoundedIcon from '@mui/icons-material/FlipRounded';
import UndoRoundedIcon from '@mui/icons-material/UndoRounded';
import RedoRoundedIcon from '@mui/icons-material/RedoRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
// Platform / crop-shape icons
import CropFreeRoundedIcon from '@mui/icons-material/CropFreeRounded';
import CropSquareRoundedIcon from '@mui/icons-material/CropSquareRounded';
import InstagramIcon from '@mui/icons-material/Instagram';
import FacebookIcon from '@mui/icons-material/Facebook';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import RedditIcon from '@mui/icons-material/Reddit';
import TelegramIcon from '@mui/icons-material/Telegram';
import PinterestIcon from '@mui/icons-material/Pinterest';
import XIcon from '@mui/icons-material/X';
import AlternateEmailRoundedIcon from '@mui/icons-material/AlternateEmailRounded';
import CameraAltRoundedIcon from '@mui/icons-material/CameraAltRounded';
import StorefrontRoundedIcon from '@mui/icons-material/StorefrontRounded';
import ForumRoundedIcon from '@mui/icons-material/ForumRounded';
import EmojiPicker from '@/components/LazyEmojiPicker';
import { PALETTE, RADII, SHADOWS } from '@/theme/tokens';
import {
  composeEditedImage, buildFilterString, createImage,
  DEFAULT_FINETUNE, FILTER_PRESETS,
} from '@/utils/imageEditor';

// ── Static config ──────────────────────────────────────────────────────────

// Crop-shape / platform presets. `label` is the canonical id (stored as
// aspectLabel); `name`/`ratio`/`Icon`/`color` drive the aligned list UI. Each
// carries the platform's recommended output pixel size (applied to Resize).
const CROP_PRESETS = [
  { key: 'custom', label: 'Custom', name: 'Custom', ratio: 'Free', value: undefined, group: 'General', Icon: CropFreeRoundedIcon, color: '#8A93A8' },
  { key: 'square', label: 'Square', name: 'Square', ratio: '1:1', value: 1, w: 1080, h: 1080, group: 'General', Icon: CropSquareRoundedIcon, color: '#8A93A8' },
  // Portrait
  { key: 'ig_portrait', label: 'Instagram Post', name: 'Instagram Post', ratio: '4:5', value: 4 / 5, w: 1080, h: 1350, group: 'Portrait', Icon: InstagramIcon, color: '#E4405F' },
  { key: 'ig_story', label: 'Instagram Story', name: 'Instagram Story', ratio: '9:16', value: 9 / 16, w: 1080, h: 1920, group: 'Portrait', Icon: InstagramIcon, color: '#E4405F' },
  { key: 'threads', label: 'Threads', name: 'Threads', ratio: '4:5', value: 4 / 5, w: 1080, h: 1350, group: 'Portrait', Icon: AlternateEmailRoundedIcon, color: '#1E2A44' },
  { key: 'pinterest', label: 'Pinterest', name: 'Pinterest', ratio: '2:3', value: 2 / 3, w: 1000, h: 1500, group: 'Portrait', Icon: PinterestIcon, color: '#BD081C' },
  { key: 'snapchat', label: 'Snapchat', name: 'Snapchat', ratio: '9:16', value: 9 / 16, w: 1080, h: 1920, group: 'Portrait', Icon: CameraAltRoundedIcon, color: '#F7B500' },
  // Landscape
  { key: 'facebook', label: 'Facebook', name: 'Facebook', ratio: '1.91', value: 1200 / 630, w: 1200, h: 630, group: 'Landscape', Icon: FacebookIcon, color: '#1877F2' },
  { key: 'linkedin', label: 'LinkedIn', name: 'LinkedIn', ratio: '1.91', value: 1200 / 627, w: 1200, h: 627, group: 'Landscape', Icon: LinkedInIcon, color: '#0A66C2' },
  { key: 'twitter', label: 'Twitter / X', name: 'Twitter / X', ratio: '16:9', value: 16 / 9, w: 1600, h: 900, group: 'Landscape', Icon: XIcon, color: '#1E2A44' },
  { key: 'gmb', label: 'Google Business', name: 'Google Business', ratio: '4:3', value: 4 / 3, w: 1200, h: 900, group: 'Landscape', Icon: StorefrontRoundedIcon, color: '#4285F4' },
  { key: 'reddit', label: 'Reddit', name: 'Reddit', ratio: '1.91', value: 1200 / 628, w: 1200, h: 628, group: 'Landscape', Icon: RedditIcon, color: '#FF4500' },
  { key: 'telegram', label: 'Telegram', name: 'Telegram', ratio: '16:9', value: 16 / 9, w: 1280, h: 720, group: 'Landscape', Icon: TelegramIcon, color: '#26A5E4' },
  { key: 'discord', label: 'Discord', name: 'Discord', ratio: '16:9', value: 16 / 9, w: 1280, h: 720, group: 'Landscape', Icon: ForumRoundedIcon, color: '#5865F2' },
];
const CROP_GROUPS = ['General', 'Portrait', 'Landscape'];

// 9-point anchor grid for positioning overlays (first char = vertical, second = horizontal).
const ANCHORS = [
  ['tl', '↖'], ['tc', '↑'], ['tr', '↗'],
  ['cl', '←'], ['cc', '•'], ['cr', '→'],
  ['bl', '↙'], ['bc', '↓'], ['br', '↘'],
];

const LIB_KEY = 'imageEditor.savedImages';
const loadLib = () => { try { return JSON.parse(localStorage.getItem(LIB_KEY) || '[]'); } catch { return []; } };
const saveLib = (arr) => { try { localStorage.setItem(LIB_KEY, JSON.stringify(arr.slice(0, 24))); } catch { /* quota */ } };

const RECENT_FONTS_KEY = 'imageEditor.recentFonts';
const loadRecentFonts = () => { try { return JSON.parse(localStorage.getItem(RECENT_FONTS_KEY) || '[]'); } catch { return []; } };
const saveRecentFonts = (arr) => { try { localStorage.setItem(RECENT_FONTS_KEY, JSON.stringify(arr.slice(0, 6))); } catch { /* quota */ } };

// Web-safe fonts render without loading. Google fonts are pulled in on demand.
const g = (name, fallback = 'sans-serif') => ({ label: name, value: `"${name}", ${fallback}`, google: name });
const FONT_GROUPS = [
  { label: 'System', fonts: [
    { label: 'Inter', value: 'Inter, -apple-system, sans-serif' },
    { label: 'Arial', value: 'Arial, Helvetica, sans-serif' },
    { label: 'Georgia', value: 'Georgia, serif' },
    { label: 'Times New Roman', value: '"Times New Roman", Times, serif' },
    { label: 'Courier New', value: '"Courier New", monospace' },
    { label: 'Verdana', value: 'Verdana, Geneva, sans-serif' },
    { label: 'Trebuchet MS', value: '"Trebuchet MS", sans-serif' },
    { label: 'Impact', value: 'Impact, Charcoal, sans-serif' },
    { label: 'Comic Sans MS', value: '"Comic Sans MS", cursive' },
  ] },
  { label: 'Sans Serif', fonts: [
    g('Roboto'), g('Open Sans'), g('Lato'), g('Montserrat'), g('Poppins'), g('Raleway'),
    g('Nunito'), g('Work Sans'), g('Rubik'), g('Manrope'), g('DM Sans'), g('Mulish'),
    g('Barlow'), g('Archivo'), g('Josefin Sans'), g('Kanit'), g('Oswald'), g('Bebas Neue'),
    g('Anton'), g('Titillium Web'),
  ] },
  { label: 'Serif', fonts: [
    g('Playfair Display', 'serif'), g('Merriweather', 'serif'), g('Lora', 'serif'), g('PT Serif', 'serif'),
    g('Roboto Slab', 'serif'), g('Bitter', 'serif'), g('EB Garamond', 'serif'), g('Libre Baskerville', 'serif'),
    g('Cormorant Garamond', 'serif'), g('Crimson Text', 'serif'), g('Noto Serif', 'serif'),
  ] },
  { label: 'Display', fonts: [
    g('Abril Fatface', 'serif'), g('Lobster', 'cursive'), g('Pacifico', 'cursive'), g('Righteous', 'cursive'),
    g('Bungee', 'cursive'), g('Alfa Slab One', 'serif'), g('Titan One', 'cursive'), g('Passion One', 'cursive'),
    g('Fredoka'), g('Comfortaa'),
  ] },
  { label: 'Handwriting', fonts: [
    g('Dancing Script', 'cursive'), g('Caveat', 'cursive'), g('Great Vibes', 'cursive'), g('Satisfy', 'cursive'),
    g('Sacramento', 'cursive'), g('Shadows Into Light', 'cursive'), g('Permanent Marker', 'cursive'),
    g('Amatic SC', 'cursive'), g('Kalam', 'cursive'), g('Indie Flower', 'cursive'),
  ] },
  { label: 'Monospace', fonts: [
    g('Roboto Mono', 'monospace'), g('Source Code Pro', 'monospace'), g('JetBrains Mono', 'monospace'),
    g('Space Mono', 'monospace'), g('Inconsolata', 'monospace'),
  ] },
];
const DEFAULT_FONT = FONT_GROUPS[0].fonts[0].value;
const FONT_BY_VALUE = Object.fromEntries(FONT_GROUPS.flatMap((gr) => gr.fonts).map((f) => [f.value, f.label]));
const ALL_GOOGLE_FONTS = FONT_GROUPS.flatMap((gr) => gr.fonts).filter((f) => f.google).map((f) => f.google);

// Inject one stylesheet per family (v1 API is lenient about missing weights) the
// first time the editor is used. Per-family links keep one bad name from
// breaking the rest. Font files download lazily when a glyph is rendered.
function ensureFontStylesheet() {
  if (typeof document === 'undefined') return;
  ALL_GOOGLE_FONTS.forEach((name) => {
    const id = `ie-font-${name.replace(/[^a-z0-9]/gi, '')}`;
    if (document.getElementById(id)) return;
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css?family=${name.replace(/ /g, '+')}:400,700&display=swap`;
    document.head.appendChild(link);
  });
}

const TEXT_COLORS = ['#FFFFFF', '#000000', '#FF3B30', '#FF9500', '#FFCC00', '#34C759', '#3D5AFE', '#AF52DE'];

const TOOLS = [
  { key: 'crop', label: 'Crop', Icon: CropRoundedIcon },
  { key: 'finetune', label: 'Finetune', Icon: TuneRoundedIcon },
  { key: 'filter', label: 'Filter', Icon: PhotoFilterRoundedIcon },
  { key: 'annotate', label: 'Annotate', Icon: GestureRoundedIcon },
  { key: 'sticker', label: 'Sticker', Icon: EmojiEmotionsRoundedIcon },
  { key: 'image', label: 'Logo', Icon: AddPhotoAlternateRoundedIcon },
  { key: 'redact', label: 'Redact', Icon: BlurOnRoundedIcon },
  { key: 'text', label: 'Text', Icon: TitleRoundedIcon },
  { key: 'resize', label: 'Resize', Icon: AspectRatioRoundedIcon },
];

const uid = () => `${Date.now().toString(36)}${Math.floor(performance.now() % 1000)}`;

// ── Component ────────────────────────────────────────────────────────────────

/**
 * Unified image editor. Props:
 *   { open, src, fileName, mode='post'|'logo', mime='image/jpeg', onClose, onCropped(blob) }
 * onCropped receives the flattened, edited image as a Blob (unchanged contract).
 */
export default function ImageEditorDialog({
  open, src, fileName, mode = 'post', mime = 'image/jpeg', onClose, onCropped,
}) {
  const isLogo = mode === 'logo';

  // Crop / transform ---------------------------------------------------------
  const [tool, setTool] = useState('crop');
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [flipH, setFlipH] = useState(false);
  const [flipV, setFlipV] = useState(false);
  const [aspect, setAspect] = useState(isLogo ? 1 : undefined);
  const [aspectLabel, setAspectLabel] = useState(isLogo ? 'Square' : 'Custom');
  const [shape, setShape] = useState(isLogo ? 'square' : 'rect'); // logo only: square|circle|rounded|rect(free)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  // Adjust -------------------------------------------------------------------
  const [finetune, setFinetune] = useState({ ...DEFAULT_FINETUNE });
  const [filterKey, setFilterKey] = useState('none');

  // Overlays -----------------------------------------------------------------
  const [overlays, setOverlays] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [annShape, setAnnShape] = useState('pen');
  const [annColor, setAnnColor] = useState('#FF3B30');
  const [redactMode, setRedactMode] = useState('blur');

  // Resize -------------------------------------------------------------------
  const [resize, setResize] = useState({ enabled: false, width: 0, height: 0, lock: true });

  // Working source (flip baked in so the crop viewport shows it live) ---------
  const [workingSrc, setWorkingSrc] = useState(src);
  const [baked, setBaked] = useState(null); // { url, w, h } cropped preview for overlay tools
  const cropDirtyRef = useRef(true);

  // Misc ---------------------------------------------------------------------
  const [busy, setBusy] = useState(false);
  const [shapeMenuEl, setShapeMenuEl] = useState(null);
  const [emojiEl, setEmojiEl] = useState(null);
  const [savedImages, setSavedImages] = useState([]);
  const [recentFonts, setRecentFonts] = useState([]);
  const fileInputRef = useRef(null);
  const stageRef = useRef(null);
  const [disp, setDisp] = useState(null); // { w, h } display size of baked image
  const drag = useRef(null);

  // History (overlays + adjustments) -----------------------------------------
  const history = useRef({ past: [], future: [] });
  const [, forceHist] = useState(0);
  const snapshot = () => ({
    overlays: JSON.parse(JSON.stringify(overlays)),
    finetune: { ...finetune }, filterKey,
    resize: { ...resize }, rotation, flipH, flipV, aspect, aspectLabel, shape,
  });
  const commit = useCallback((prev) => {
    history.current.past.push(prev);
    if (history.current.past.length > 40) history.current.past.shift();
    history.current.future = [];
    forceHist((n) => n + 1);
  }, []);

  const selected = overlays.find((o) => o.id === selectedId) || null;

  // ── Reset on open ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    ensureFontStylesheet();
    setTool('crop');
    setCrop({ x: 0, y: 0 }); setZoom(1); setRotation(0);
    setFlipH(false); setFlipV(false);
    setAspect(isLogo ? 1 : undefined); setAspectLabel(isLogo ? 'Square' : 'Custom');
    setShape(isLogo ? 'square' : 'rect');
    setCroppedAreaPixels(null);
    setFinetune({ ...DEFAULT_FINETUNE }); setFilterKey('none');
    setOverlays([]); setSelectedId(null);
    setResize({ enabled: false, width: 0, height: 0, lock: true });
    setWorkingSrc(src); setBaked(null);
    cropDirtyRef.current = true;
    history.current = { past: [], future: [] };
    setSavedImages(loadLib());
    setRecentFonts(loadRecentFonts());
    setBusy(false);
  }, [open, src, isLogo]);

  // ── Flip → rebuild workingSrc from the ORIGINAL (avoids double-flip) ───────
  useEffect(() => {
    if (!open || !src) return;
    let cancelled = false;
    (async () => {
      if (!flipH && !flipV) { if (!cancelled) setWorkingSrc(src); return; }
      try {
        const img = await createImage(src);
        const c = document.createElement('canvas');
        c.width = img.width; c.height = img.height;
        const ctx = c.getContext('2d');
        ctx.translate(flipH ? img.width : 0, flipV ? img.height : 0);
        ctx.scale(flipH ? -1 : 1, flipV ? -1 : 1);
        ctx.drawImage(img, 0, 0);
        if (!cancelled) { setWorkingSrc(c.toDataURL('image/png')); cropDirtyRef.current = true; }
      } catch { /* keep previous */ }
    })();
    return () => { cancelled = true; };
  }, [flipH, flipV, src, open]);

  const onCropComplete = useCallback((_, areaPx) => {
    setCroppedAreaPixels(areaPx);
    cropDirtyRef.current = true;
  }, []);

  // ── Bake cropped preview when leaving the Crop tool ───────────────────────
  const bakeBase = useCallback(async () => {
    if (!workingSrc || !croppedAreaPixels) return;
    try {
      const img = await createImage(workingSrc);
      // Replicate compositor's rotate+crop to a canvas → dataURL for the stage.
      const blob = await composeEditedImage(workingSrc, {
        crop: croppedAreaPixels, rotation, mime: 'image/png',
      });
      const url = URL.createObjectURL(blob);
      const bimg = await createImage(url);
      setBaked((prev) => { if (prev?.url) URL.revokeObjectURL(prev.url); return { url, w: bimg.width, h: bimg.height }; });
      if (!resize.enabled) setResize((r) => ({ ...r, width: bimg.width, height: bimg.height }));
      cropDirtyRef.current = false;
      void img;
    } catch { /* ignore */ }
  }, [workingSrc, croppedAreaPixels, rotation, resize.enabled]);

  const selectTool = async (key) => {
    if (key !== 'crop' && cropDirtyRef.current) await bakeBase();
    setSelectedId(null);
    setTool(key);
  };

  // ── Measure the baked stage display size ──────────────────────────────────
  const measure = useCallback(() => {
    const el = stageRef.current;
    if (!el || !baked) return;
    const cw = el.clientWidth; const ch = el.clientHeight;
    const ar = baked.w / baked.h;
    let w; let h;
    if (cw / ch > ar) { h = ch; w = ch * ar; } else { w = cw; h = cw / ar; }
    setDisp({ w, h });
  }, [baked]);
  useEffect(() => { measure(); }, [measure, tool]);
  useEffect(() => {
    if (!open) return undefined;
    const onR = () => measure();
    window.addEventListener('resize', onR);
    return () => window.removeEventListener('resize', onR);
  }, [open, measure]);

  // ── Crop-shape / aspect selection ─────────────────────────────────────────
  const pickAspect = (label, value, size) => {
    setAspect(value); setAspectLabel(label);
    if (isLogo && value !== 1) setShape('rect');
    // Platform presets also set the recommended output pixel size.
    if (size && size.w && size.h) setResize({ enabled: true, width: size.w, height: size.h, lock: true });
    setShapeMenuEl(null);
    cropDirtyRef.current = true;
  };
  const pickLogoShape = (s) => {
    setShape(s);
    if (s !== 'rect') { setAspect(1); setAspectLabel('Square'); }
    setShapeMenuEl(null);
    cropDirtyRef.current = true;
  };
  const applyDimensions = (w, h) => {
    const nw = Number(w); const nh = Number(h);
    if (nw > 0 && nh > 0) { setAspect(nw / nh); setAspectLabel(`${nw}×${nh}`); cropDirtyRef.current = true; }
  };
  const rotateLeft = () => { const prev = snapshot(); setRotation((r) => (r - 90) % 360); cropDirtyRef.current = true; commit(prev); };

  // ── Undo / redo ───────────────────────────────────────────────────────────
  const applySnap = (s) => {
    setOverlays(s.overlays); setFinetune(s.finetune); setFilterKey(s.filterKey);
    setResize(s.resize); setRotation(s.rotation); setFlipH(s.flipH); setFlipV(s.flipV);
    setAspect(s.aspect); setAspectLabel(s.aspectLabel); setShape(s.shape);
    cropDirtyRef.current = true;
  };
  const undo = () => {
    const h = history.current;
    if (!h.past.length) return;
    h.future.push(snapshot());
    applySnap(h.past.pop());
    forceHist((n) => n + 1);
  };
  const redo = () => {
    const h = history.current;
    if (!h.future.length) return;
    h.past.push(snapshot());
    applySnap(h.future.pop());
    forceHist((n) => n + 1);
  };

  // ── Overlay helpers ───────────────────────────────────────────────────────
  const addOverlay = (o) => { const prev = snapshot(); setOverlays((cur) => [...cur, o]); setSelectedId(o.id); commit(prev); };
  const updateOverlay = (id, patch) => setOverlays((cur) => cur.map((o) => (o.id === id ? { ...o, ...patch } : o)));
  const removeOverlay = (id) => { const prev = snapshot(); setOverlays((cur) => cur.filter((o) => o.id !== id)); setSelectedId(null); commit(prev); };

  const addText = () => addOverlay({
    id: uid(), type: 'text', text: 'Double-click to edit', x: 0.5, y: 0.5,
    fontFamily: DEFAULT_FONT, fontSize: 0.09, weight: 800, color: '#FFFFFF',
    align: 'center', bg: null, rotation: 0,
  });
  const addSticker = (char) => addOverlay({ id: uid(), type: 'sticker', char, x: 0.5, y: 0.5, size: 0.18, rotation: 0 });
  const addImageOverlay = (imgSrc) => addOverlay({ id: uid(), type: 'sticker', src: imgSrc, x: 0.5, y: 0.5, size: 0.3, rotation: 0 });

  // Upload a logo / image → add as overlay AND persist to the saved library.
  const onUploadImage = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const imgSrc = reader.result;
      addImageOverlay(imgSrc);
      const next = [{ id: uid(), src: imgSrc }, ...savedImages].slice(0, 24);
      setSavedImages(next); saveLib(next);
    };
    reader.readAsDataURL(file);
  };
  const removeSaved = (id) => { const next = savedImages.filter((s) => s.id !== id); setSavedImages(next); saveLib(next); };

  // Change the selected text's font and remember it in the recent list.
  const pickFont = (value) => {
    if (selected?.type === 'text') updateOverlay(selected.id, { fontFamily: value });
    setRecentFonts((cur) => { const next = [value, ...cur.filter((v) => v !== value)].slice(0, 6); saveRecentFonts(next); return next; });
  };

  // Snap the selected overlay to one of the 9 anchor points.
  const alignOverlay = (anchor) => {
    if (!selected || !baked) return;
    const prev = snapshot();
    const minWH = Math.min(baked.w, baked.h);
    const norm = selected.type === 'sticker' ? (selected.size || 0.2) * minWH : (selected.fontSize || 0.1) * baked.h * 4;
    const halfX = Math.min(0.45, norm / baked.w / 2 + 0.02);
    const halfY = Math.min(0.45, norm / baked.h / 2 + 0.02);
    const m = 0.04;
    const xMap = { l: m + halfX, c: 0.5, r: 1 - m - halfX };
    const yMap = { t: m + halfY, c: 0.5, b: 1 - m - halfY };
    updateOverlay(selected.id, { x: xMap[anchor[1]], y: yMap[anchor[0]] });
    commit(prev);
  };

  // ── Pointer drag for moving/resizing overlays on the stage ────────────────
  const startOverlayDrag = (e, o, handle) => {
    e.preventDefault(); e.stopPropagation();
    if (!disp) return;
    setSelectedId(o.id);
    const prev = snapshot();
    drag.current = { id: o.id, handle, startX: e.clientX, startY: e.clientY, o: { ...o }, committed: false, prev };
    window.addEventListener('pointermove', onOverlayMove);
    window.addEventListener('pointerup', onOverlayUp);
  };
  const onOverlayMove = (e) => {
    const d = drag.current; if (!d || !disp) return;
    const dxN = (e.clientX - d.startX) / disp.w;
    const dyN = (e.clientY - d.startY) / disp.h;
    if (d.handle === 'move') {
      updateOverlay(d.id, {
        x: Math.min(1, Math.max(0, d.o.x + dxN)),
        y: Math.min(1, Math.max(0, d.o.y + dyN)),
      });
    } else if (d.handle === 'size') {
      if (d.o.type === 'sticker') {
        updateOverlay(d.id, { size: Math.max(0.03, d.o.size + dxN) });
      } else if (d.o.type === 'text') {
        updateOverlay(d.id, { fontSize: Math.max(0.02, d.o.fontSize + dyN) });
      } else if (d.o.type === 'redact') {
        updateOverlay(d.id, { w: Math.max(0.02, d.o.w + dxN), h: Math.max(0.02, d.o.h + dyN) });
      }
    }
  };
  const onOverlayUp = () => {
    const d = drag.current;
    window.removeEventListener('pointermove', onOverlayMove);
    window.removeEventListener('pointerup', onOverlayUp);
    if (d && !d.committed) commit(d.prev);
    drag.current = null;
  };

  // ── Draw new annotate/redact shapes directly on the stage ─────────────────
  const stageDraw = useRef(null);
  const onStagePointerDown = (e) => {
    if (tool !== 'annotate' && tool !== 'redact') { setSelectedId(null); return; }
    const box = e.currentTarget.getBoundingClientRect();
    const nx = (e.clientX - box.left) / box.width;
    const ny = (e.clientY - box.top) / box.height;
    if (tool === 'redact') {
      const o = { id: uid(), type: 'redact', x: nx, y: ny, w: 0.001, h: 0.001, mode: redactMode, color: '#000' };
      stageDraw.current = { o, ox: nx, oy: ny, prev: snapshot() };
      setOverlays((cur) => [...cur, o]); setSelectedId(o.id);
    } else {
      const o = { id: uid(), type: 'stroke', shape: annShape, color: annColor, stroke: 0.008, points: [{ x: nx, y: ny }] };
      stageDraw.current = { o, ox: nx, oy: ny, prev: snapshot() };
      setOverlays((cur) => [...cur, o]); setSelectedId(o.id);
    }
    window.addEventListener('pointermove', onStageDrawMove);
    window.addEventListener('pointerup', onStageDrawUp);
  };
  const onStageDrawMove = (e) => {
    const d = stageDraw.current; if (!d || !stageRef.current) return;
    const inner = stageRef.current.querySelector('[data-stage-inner]');
    const box = (inner || stageRef.current).getBoundingClientRect();
    const nx = Math.min(1, Math.max(0, (e.clientX - box.left) / box.width));
    const ny = Math.min(1, Math.max(0, (e.clientY - box.top) / box.height));
    setOverlays((cur) => cur.map((o) => {
      if (o.id !== d.o.id) return o;
      if (o.type === 'redact') return { ...o, x: Math.min(d.ox, nx), y: Math.min(d.oy, ny), w: Math.abs(nx - d.ox), h: Math.abs(ny - d.oy) };
      if (o.shape === 'pen') return { ...o, points: [...o.points, { x: nx, y: ny }] };
      return { ...o, points: [{ x: d.ox, y: d.oy }, { x: nx, y: ny }] };
    }));
  };
  const onStageDrawUp = () => {
    const d = stageDraw.current;
    window.removeEventListener('pointermove', onStageDrawMove);
    window.removeEventListener('pointerup', onStageDrawUp);
    if (d) commit(d.prev);
    stageDraw.current = null;
  };

  // ── Export ────────────────────────────────────────────────────────────────
  const presetCss = useMemo(() => FILTER_PRESETS.find((p) => p.key === filterKey)?.css || '', [filterKey]);
  const previewFilter = useMemo(() => buildFilterString(finetune, presetCss), [finetune, presetCss]);

  const handleDone = async () => {
    if (!croppedAreaPixels || !workingSrc) return;
    setBusy(true);
    try {
      const blob = await composeEditedImage(workingSrc, {
        crop: croppedAreaPixels,
        rotation,
        flipH: false, flipV: false, // already baked into workingSrc
        finetune, presetCss, overlays,
        shape: isLogo ? shape : 'rect',
        resize: resize.enabled && resize.width && resize.height ? { width: resize.width, height: resize.height } : null,
        mime,
      });
      onCropped?.(blob, aspectLabel);
      onClose?.();
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert('Could not export this image.');
    } finally {
      setBusy(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { borderRadius: `${RADII.card}px`, boxShadow: SHADOWS.popover, overflow: 'hidden', height: '88vh' } }}
    >
      {/* Header */}
      <Box sx={{ px: 2.5, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: `1px solid ${PALETTE.divider}` }}>
        <Box>
          <Typography sx={{ fontSize: 15, fontWeight: 800, color: PALETTE.heading }}>Image editor</Typography>
          {fileName && <Typography sx={{ fontSize: 11, color: PALETTE.muted }} noWrap>{fileName}</Typography>}
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Tooltip title="Undo"><span><IconButton size="small" disabled={!history.current.past.length} onClick={undo}><UndoRoundedIcon fontSize="small" /></IconButton></span></Tooltip>
          <Tooltip title="Redo"><span><IconButton size="small" disabled={!history.current.future.length} onClick={redo}><RedoRoundedIcon fontSize="small" /></IconButton></span></Tooltip>
          <Button onClick={handleDone} disabled={busy || !croppedAreaPixels} variant="contained" disableElevation startIcon={busy ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <CheckRoundedIcon />} sx={{ textTransform: 'none', fontWeight: 800, borderRadius: `${RADII.pill}px`, px: 2.5 }}>
            {busy ? 'Saving…' : 'Done'}
          </Button>
          <IconButton size="small" onClick={busy ? undefined : onClose}><CloseRoundedIcon fontSize="small" /></IconButton>
        </Stack>
      </Box>

      <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
        {/* Left tool rail */}
        <Box sx={{ width: 88, flexShrink: 0, borderRight: `1px solid ${PALETTE.divider}`, py: 1, overflowY: 'auto', bgcolor: PALETTE.secondaryNav }}>
          {TOOLS.map(({ key, label, Icon }) => {
            const active = tool === key;
            return (
              <Box
                key={key}
                onClick={() => selectTool(key)}
                sx={{
                  mx: 1, mb: 0.5, py: 1, borderRadius: `${RADII.base}px`, cursor: 'pointer',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.4,
                  color: active ? PALETTE.primary : PALETTE.body,
                  bgcolor: active ? PALETTE.primarySoft : 'transparent',
                  '&:hover': { bgcolor: active ? PALETTE.primarySoft : PALETTE.inputFill },
                }}
              >
                <Icon sx={{ fontSize: 20 }} />
                <Typography sx={{ fontSize: 10, fontWeight: 700 }}>{label}</Typography>
              </Box>
            );
          })}
        </Box>

        {/* Center: toolbar + stage */}
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          {/* Top action strip */}
          <Stack direction="row" spacing={1} alignItems="center" sx={{ px: 2, py: 1, borderBottom: `1px solid ${PALETTE.divider}` }}>
            <Tooltip title="Rotate left"><IconButton size="small" onClick={rotateLeft}><RotateLeftRoundedIcon fontSize="small" /></IconButton></Tooltip>
            <Tooltip title="Flip horizontal"><IconButton size="small" color={flipH ? 'primary' : 'default'} onClick={() => setFlipH((v) => !v)}><FlipRoundedIcon fontSize="small" /></IconButton></Tooltip>
            <Tooltip title="Flip vertical"><IconButton size="small" color={flipV ? 'primary' : 'default'} onClick={() => setFlipV((v) => !v)}><FlipRoundedIcon fontSize="small" sx={{ transform: 'rotate(90deg)' }} /></IconButton></Tooltip>
            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
            <Button size="small" onClick={(e) => setShapeMenuEl(e.currentTarget)} endIcon={<ExpandMoreRoundedIcon />} startIcon={<CropRoundedIcon />} sx={{ textTransform: 'none', fontWeight: 700, color: PALETTE.body }}>
              {isLogo && shape !== 'rect' ? shape[0].toUpperCase() + shape.slice(1) : aspectLabel}
            </Button>
          </Stack>

          {/* Stage */}
          <Box sx={{ flex: 1, minHeight: 0, position: 'relative', bgcolor: '#0B1020' }}>
            {tool === 'crop' ? (
              workingSrc && (
                <Cropper
                  image={workingSrc}
                  crop={crop}
                  zoom={zoom}
                  rotation={rotation}
                  aspect={aspect}
                  cropShape={isLogo && shape === 'circle' ? 'round' : 'rect'}
                  objectFit="contain"
                  restrictPosition={false}
                  showGrid
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onRotationChange={setRotation}
                  onCropComplete={onCropComplete}
                />
              )
            ) : (
              <Box
                ref={stageRef}
                onPointerDown={onStagePointerDown}
                sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', touchAction: 'none', cursor: (tool === 'annotate' || tool === 'redact') ? 'crosshair' : 'default' }}
              >
                {baked && disp && (
                  <Box data-stage-inner sx={{ position: 'relative', width: disp.w, height: disp.h }}>
                    <Box component="img" src={baked.url} alt="preview" draggable={false} sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', filter: previewFilter, userSelect: 'none' }} />
                    {/* SVG layer for strokes */}
                    <Box component="svg" viewBox="0 0 100 100" preserveAspectRatio="none" sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}>
                      {overlays.filter((o) => o.type === 'stroke').map((o) => <StrokeSvg key={o.id} o={o} />)}
                    </Box>
                    {/* Redact + text + sticker as positioned elements */}
                    {overlays.filter((o) => o.type !== 'stroke').map((o) => (
                      <OverlayEl
                        key={o.id}
                        o={o}
                        disp={disp}
                        selected={selectedId === o.id}
                        filter={previewFilter}
                        onDragStart={startOverlayDrag}
                        onChangeText={(txt) => updateOverlay(o.id, { text: txt })}
                      />
                    ))}
                  </Box>
                )}
              </Box>
            )}
          </Box>

          {/* Crop bottom controls */}
          {tool === 'crop' && (
            <Stack direction="row" spacing={3} alignItems="center" sx={{ px: 3, py: 1.5, borderTop: `1px solid ${PALETTE.divider}` }}>
              <Stack sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: 10, fontWeight: 700, color: PALETTE.muted, textTransform: 'uppercase', letterSpacing: '.05em' }}>Rotation · {rotation}°</Typography>
                <Slider value={rotation} min={-180} max={180} step={1} onChange={(_, v) => { setRotation(v); cropDirtyRef.current = true; }} size="small" />
              </Stack>
              <Stack sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: 10, fontWeight: 700, color: PALETTE.muted, textTransform: 'uppercase', letterSpacing: '.05em' }}>Scale</Typography>
                <Slider value={zoom} min={1} max={3} step={0.02} onChange={(_, v) => setZoom(v)} size="small" />
              </Stack>
            </Stack>
          )}
        </Box>

        {/* Right control panel */}
        <Box sx={{ width: 272, flexShrink: 0, borderLeft: `1px solid ${PALETTE.divider}`, p: 2, overflowY: 'auto' }}>
          {tool === 'crop' && (
            <CropPanel isLogo={isLogo} aspectLabel={aspectLabel} shape={shape} onDimensions={applyDimensions} onPickAspect={pickAspect} onPickShape={pickLogoShape} />
          )}
          {tool === 'image' && (
            <ImagePanel
              saved={savedImages}
              selected={selected?.type === 'sticker' && selected.src ? selected : null}
              onUploadClick={() => fileInputRef.current?.click()}
              onAddSaved={(s) => addImageOverlay(s.src)}
              onRemoveSaved={removeSaved}
              onUpdate={(patch) => selected && updateOverlay(selected.id, patch)}
              onAlign={alignOverlay}
              onDelete={() => selected && removeOverlay(selected.id)}
            />
          )}
          {tool === 'finetune' && (
            <FinetunePanel finetune={finetune} setFinetune={(f) => { setFinetune(f); }} onCommit={commit} snapshot={snapshot} onReset={() => { const p = snapshot(); setFinetune({ ...DEFAULT_FINETUNE }); commit(p); }} />
          )}
          {tool === 'filter' && (
            <FilterPanel filterKey={filterKey} onPick={(k) => { const p = snapshot(); const preset = FILTER_PRESETS.find((x) => x.key === k); setFilterKey(k); setFinetune({ ...DEFAULT_FINETUNE, ...(preset?.finetune || {}) }); commit(p); }} baked={baked} />
          )}
          {tool === 'annotate' && (
            <AnnotatePanel annShape={annShape} setAnnShape={setAnnShape} annColor={annColor} setAnnColor={setAnnColor} />
          )}
          {tool === 'sticker' && (
            <StickerPanel
              selected={selected?.type === 'sticker' && !selected.src ? selected : null}
              onAddClick={(e) => setEmojiEl(e.currentTarget)}
              onUpdate={(patch) => selected && updateOverlay(selected.id, patch)}
              onAlign={alignOverlay}
              onDelete={() => selected && removeOverlay(selected.id)}
            />
          )}
          {tool === 'redact' && (
            <Box>
              <PanelTitle>Redact</PanelTitle>
              <ToggleButtonGroup exclusive size="small" fullWidth value={redactMode} onChange={(_, v) => v && setRedactMode(v)} sx={{ mb: 1 }}>
                <ToggleButton value="blur" sx={{ textTransform: 'none', fontWeight: 700 }}>Blur</ToggleButton>
                <ToggleButton value="pixelate" sx={{ textTransform: 'none', fontWeight: 700 }}>Pixel</ToggleButton>
                <ToggleButton value="block" sx={{ textTransform: 'none', fontWeight: 700 }}>Block</ToggleButton>
              </ToggleButtonGroup>
              <Typography sx={{ fontSize: 11, color: PALETTE.muted }}>Drag on the image to cover an area.</Typography>
            </Box>
          )}
          {tool === 'text' && (
            <TextPanel selected={selected?.type === 'text' ? selected : null} recentFonts={recentFonts} onAdd={addText} onUpdate={(patch) => updateOverlay(selected.id, patch)} onFontChange={pickFont} onAlign={alignOverlay} onDelete={() => selected && removeOverlay(selected.id)} />
          )}
          {tool === 'resize' && (
            <ResizePanel resize={resize} baked={baked} setResize={setResize} />
          )}

          {/* Quick delete for overlays without a dedicated panel (redact / annotation) */}
          {selected && (selected.type === 'redact' || selected.type === 'stroke') && (
            <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${PALETTE.divider}` }}>
              <Button fullWidth color="error" size="small" startIcon={<DeleteOutlineRoundedIcon />} onClick={() => removeOverlay(selected.id)} sx={{ textTransform: 'none', fontWeight: 700 }}>Delete selected</Button>
            </Box>
          )}
        </Box>
      </Box>

      {/* Crop-shape menu */}
      <Menu anchorEl={shapeMenuEl} open={Boolean(shapeMenuEl)} onClose={() => setShapeMenuEl(null)} PaperProps={{ sx: { maxHeight: 440 } }}>
        {CROP_GROUPS.map((g) => [
          <ListSubheader key={g} sx={{ fontSize: 10, fontWeight: 800, letterSpacing: '.08em', lineHeight: 2 }}>{g.toUpperCase()}</ListSubheader>,
          ...CROP_PRESETS.filter((p) => p.group === g).map((p) => {
            const { Icon } = p;
            return (
              <MenuItem key={p.key} selected={aspectLabel === p.label} onClick={() => pickAspect(p.label, p.value, { w: p.w, h: p.h })} sx={{ gap: 1.25, fontSize: 13, fontWeight: 600 }}>
                <Icon sx={{ fontSize: 17, color: p.color }} />
                <Box sx={{ flex: 1 }}>{p.name}</Box>
                <Typography sx={{ fontSize: 10, fontWeight: 700, color: PALETTE.muted }}>{p.ratio}</Typography>
              </MenuItem>
            );
          }),
        ])}
        {isLogo && [
          <Divider key="d" />,
          <ListSubheader key="sh" sx={{ fontSize: 10, fontWeight: 800, letterSpacing: '.08em', lineHeight: 2 }}>LOGO SHAPE</ListSubheader>,
          <MenuItem key="circle" onClick={() => pickLogoShape('circle')}>Circle</MenuItem>,
          <MenuItem key="rounded" onClick={() => pickLogoShape('rounded')}>Rounded</MenuItem>,
          <MenuItem key="sq" onClick={() => pickLogoShape('square')}>Square (sharp)</MenuItem>,
        ]}
      </Menu>

      {/* Emoji picker */}
      <Popover open={Boolean(emojiEl)} anchorEl={emojiEl} onClose={() => setEmojiEl(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
        <EmojiPicker onEmojiClick={(d) => { addSticker(d.emoji); setEmojiEl(null); }} height={360} width={300} />
      </Popover>

      {/* Hidden file input for logo / image upload */}
      <input ref={fileInputRef} type="file" accept="image/*" hidden onChange={onUploadImage} />
    </Dialog>
  );
}

// ── Sub-panels & overlay elements ────────────────────────────────────────────

function PanelTitle({ children }) {
  return <Typography sx={{ fontSize: 12, fontWeight: 800, color: PALETTE.heading, mb: 1.25, textTransform: 'uppercase', letterSpacing: '.04em' }}>{children}</Typography>;
}

function CropPanel({ isLogo, aspectLabel, shape, onDimensions, onPickAspect, onPickShape }) {
  const [w, setW] = useState('');
  const [h, setH] = useState('');
  return (
    <Box>
      <PanelTitle>Crop shape &amp; platform size</PanelTitle>
      {CROP_GROUPS.map((g) => (
        <Box key={g} sx={{ mb: 1.5 }}>
          <Typography sx={{ fontSize: 10, fontWeight: 800, color: PALETTE.muted, letterSpacing: '.08em', mb: 0.6 }}>{g.toUpperCase()}</Typography>
          <Stack spacing={0.5}>
            {CROP_PRESETS.filter((p) => p.group === g).map((p) => {
              const active = aspectLabel === p.label && !(isLogo && shape !== 'rect');
              const { Icon } = p;
              return (
                <Box
                  key={p.key}
                  role="button"
                  onClick={() => onPickAspect(p.label, p.value, { w: p.w, h: p.h })}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1, height: 38, px: 1, borderRadius: `${RADII.base}px`,
                    cursor: 'pointer', userSelect: 'none', transition: 'background .12s, border-color .12s',
                    border: `1px solid ${active ? PALETTE.primary : PALETTE.divider}`,
                    bgcolor: active ? PALETTE.primary : PALETTE.surface,
                    '&:hover': active ? {} : { borderColor: PALETTE.primary, bgcolor: PALETTE.primarySoft },
                  }}
                >
                  <Icon sx={{ fontSize: 17, flexShrink: 0, color: active ? '#fff' : p.color }} />
                  <Typography noWrap sx={{ flex: 1, minWidth: 0, fontSize: 12.5, fontWeight: 700, color: active ? '#fff' : PALETTE.heading }}>{p.name}</Typography>
                  <Box sx={{ px: 0.6, py: '1px', borderRadius: 1, flexShrink: 0, bgcolor: active ? 'rgba(255,255,255,.22)' : PALETTE.inputFill }}>
                    <Typography sx={{ fontSize: 9.5, fontWeight: 800, letterSpacing: '.02em', color: active ? '#fff' : PALETTE.muted }}>{p.ratio}</Typography>
                  </Box>
                  <Typography sx={{ width: 58, flexShrink: 0, textAlign: 'right', fontSize: 10, fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: active ? 'rgba(255,255,255,.85)' : PALETTE.muted }}>
                    {p.w ? `${p.w}×${p.h}` : ''}
                  </Typography>
                </Box>
              );
            })}
          </Stack>
        </Box>
      ))}
      {isLogo && (
        <>
          <PanelTitle>Logo mask</PanelTitle>
          <ToggleButtonGroup exclusive size="small" fullWidth value={shape} onChange={(_, v) => v && onPickShape(v)} sx={{ mb: 2 }}>
            <ToggleButton value="rect" sx={{ textTransform: 'none', fontWeight: 700 }}>Free</ToggleButton>
            <ToggleButton value="square" sx={{ textTransform: 'none', fontWeight: 700 }}>Square</ToggleButton>
            <ToggleButton value="circle" sx={{ textTransform: 'none', fontWeight: 700 }}>Circle</ToggleButton>
            <ToggleButton value="rounded" sx={{ textTransform: 'none', fontWeight: 700 }}>Round</ToggleButton>
          </ToggleButtonGroup>
        </>
      )}
      <PanelTitle>Custom dimensions</PanelTitle>
      <Stack direction="row" spacing={1} alignItems="center">
        <TextField size="small" type="number" placeholder="W" value={w} onChange={(e) => setW(e.target.value)} sx={{ width: 84 }} />
        <Typography sx={{ color: PALETTE.muted }}>×</Typography>
        <TextField size="small" type="number" placeholder="H" value={h} onChange={(e) => setH(e.target.value)} sx={{ width: 84 }} />
        <Button size="small" variant="outlined" onClick={() => onDimensions(w, h)} sx={{ textTransform: 'none', fontWeight: 700 }}>Set</Button>
      </Stack>
    </Box>
  );
}

function AdjSlider({ label, value, min, max, onChange, onCommitStart }) {
  return (
    <Box sx={{ mb: 1 }}>
      <Typography sx={{ fontSize: 11, fontWeight: 700, color: PALETTE.body, display: 'flex', justifyContent: 'space-between' }}><span>{label}</span><span style={{ color: PALETTE.muted }}>{value}</span></Typography>
      <Slider value={value} min={min} max={max} onChangeCommitted={onCommitStart} onChange={(_, v) => onChange(v)} size="small" />
    </Box>
  );
}

function FinetunePanel({ finetune, setFinetune, onReset, snapshot, onCommit }) {
  const startRef = useRef(null);
  const begin = () => { if (!startRef.current) startRef.current = snapshot(); };
  const end = () => { if (startRef.current) { onCommit(startRef.current); startRef.current = null; } };
  const set = (k) => (v) => { begin(); setFinetune({ ...finetune, [k]: v }); };
  return (
    <Box>
      <PanelTitle>Finetune</PanelTitle>
      <div onPointerDown={begin} onPointerUp={end}>
        <AdjSlider label="Brightness" value={finetune.brightness} min={40} max={160} onChange={set('brightness')} />
        <AdjSlider label="Contrast" value={finetune.contrast} min={40} max={160} onChange={set('contrast')} />
        <AdjSlider label="Saturation" value={finetune.saturation} min={0} max={200} onChange={set('saturation')} />
        <AdjSlider label="Warmth" value={finetune.warmth} min={-100} max={100} onChange={set('warmth')} />
        <AdjSlider label="Blur" value={finetune.blur} min={0} max={12} onChange={set('blur')} />
      </div>
      <Button size="small" onClick={onReset} sx={{ textTransform: 'none', fontWeight: 700, mt: 1 }}>Reset</Button>
    </Box>
  );
}

function FilterPanel({ filterKey, onPick, baked }) {
  return (
    <Box>
      <PanelTitle>Filters</PanelTitle>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
        {FILTER_PRESETS.map((p) => {
          const active = filterKey === p.key;
          return (
            <Box key={p.key} onClick={() => onPick(p.key)} sx={{ cursor: 'pointer', borderRadius: `${RADII.base}px`, overflow: 'hidden', border: `2px solid ${active ? PALETTE.primary : 'transparent'}` }}>
              <Box sx={{ height: 52, bgcolor: '#0B1020', backgroundImage: baked ? `url(${baked.url})` : 'none', backgroundSize: 'cover', backgroundPosition: 'center', filter: buildFilterString(p.finetune, p.css) }} />
              <Typography sx={{ fontSize: 10, fontWeight: 700, textAlign: 'center', py: 0.4, color: active ? PALETTE.primary : PALETTE.body }}>{p.label}</Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

function AnnotatePanel({ annShape, setAnnShape, annColor, setAnnColor }) {
  const shapes = [['pen', 'Pen'], ['line', 'Line'], ['arrow', 'Arrow'], ['rect', 'Box'], ['ellipse', 'Oval']];
  return (
    <Box>
      <PanelTitle>Annotate</PanelTitle>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.75, mb: 1.5 }}>
        {shapes.map(([k, lbl]) => (
          <Button key={k} size="small" variant={annShape === k ? 'contained' : 'outlined'} disableElevation onClick={() => setAnnShape(k)} sx={{ textTransform: 'none', fontWeight: 700, ...(annShape === k ? {} : { borderColor: PALETTE.divider, color: PALETTE.body }) }}>{lbl}</Button>
        ))}
      </Box>
      <ColorRow value={annColor} onChange={setAnnColor} />
      <Typography sx={{ fontSize: 11, color: PALETTE.muted, mt: 1 }}>Drag on the image to draw.</Typography>
    </Box>
  );
}

function ColorRow({ value, onChange }) {
  return (
    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
      {TEXT_COLORS.map((c) => (
        <Box key={c} onClick={() => onChange(c)} sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: c, cursor: 'pointer', border: `2px solid ${value === c ? PALETTE.primary : PALETTE.divider}`, boxShadow: c === '#FFFFFF' ? 'inset 0 0 0 1px #ddd' : 'none' }} />
      ))}
    </Stack>
  );
}

function TextPanel({ selected, recentFonts = [], onAdd, onUpdate, onFontChange, onAlign, onDelete }) {
  return (
    <Box>
      <PanelTitle>Text</PanelTitle>
      <Button fullWidth variant="contained" disableElevation startIcon={<AddRoundedIcon />} onClick={onAdd} sx={{ textTransform: 'none', fontWeight: 800, mb: 2 }}>Add text</Button>
      {!selected ? (
        <Typography sx={{ fontSize: 11, color: PALETTE.muted }}>Add text, then select it on the image to edit its content, font and colour.</Typography>
      ) : (
        <SelectedCard title="SELECTED TEXT">
          <TextField size="small" label="Text" multiline minRows={2} value={selected.text} onChange={(e) => onUpdate({ text: e.target.value })} />
          <TextField
            size="small"
            select
            label="Font"
            value={selected.fontFamily}
            onChange={(e) => (onFontChange ? onFontChange(e.target.value) : onUpdate({ fontFamily: e.target.value }))}
            SelectProps={{
              MenuProps: { PaperProps: { sx: { maxHeight: 380 } } },
              renderValue: (v) => <span style={{ fontFamily: v }}>{FONT_BY_VALUE[v] || 'Font'}</span>,
            }}
          >
            {recentFonts.length > 0 && [
              <ListSubheader key="__recent" sx={{ fontSize: 10, fontWeight: 800, letterSpacing: '.08em', lineHeight: 2.2, color: PALETTE.muted }}>RECENT</ListSubheader>,
              ...recentFonts.map((v) => (
                <MenuItem key={`r-${v}`} value={v} sx={{ fontFamily: v, fontSize: 15, py: 0.75 }}>{FONT_BY_VALUE[v] || 'Font'}</MenuItem>
              )),
            ]}
            {FONT_GROUPS.flatMap((gr) => [
              <ListSubheader key={gr.label} sx={{ fontSize: 10, fontWeight: 800, letterSpacing: '.08em', lineHeight: 2.2, color: PALETTE.muted }}>{gr.label.toUpperCase()}</ListSubheader>,
              ...gr.fonts.map((f) => (
                <MenuItem key={f.label} value={f.value} sx={{ fontFamily: f.value, fontSize: 15, py: 0.75 }}>{f.label}</MenuItem>
              )),
            ])}
          </TextField>
          <SliderRow label="Size" value={selected.fontSize} min={0.03} max={0.3} step={0.005} onChange={(_, v) => onUpdate({ fontSize: v })}>
            <NumField value={Math.round(selected.fontSize * 100)} min={2} max={50} suffix="%" onChange={(n) => onUpdate({ fontSize: Math.min(0.5, Math.max(0.02, n / 100)) })} />
          </SliderRow>
          <ToggleButtonGroup exclusive size="small" fullWidth value={selected.weight} onChange={(_, v) => v && onUpdate({ weight: v })}>
            <ToggleButton value={400} sx={{ textTransform: 'none', fontWeight: 700 }}>Body</ToggleButton>
            <ToggleButton value={800} sx={{ textTransform: 'none', fontWeight: 800 }}>Heading</ToggleButton>
          </ToggleButtonGroup>
          <ToggleButtonGroup exclusive size="small" fullWidth value={selected.align} onChange={(_, v) => v && onUpdate({ align: v })}>
            <ToggleButton value="left" sx={{ textTransform: 'none', fontWeight: 700 }}>Left</ToggleButton>
            <ToggleButton value="center" sx={{ textTransform: 'none', fontWeight: 700 }}>Center</ToggleButton>
            <ToggleButton value="right" sx={{ textTransform: 'none', fontWeight: 700 }}>Right</ToggleButton>
          </ToggleButtonGroup>
          <Box>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: PALETTE.body, mb: 0.5 }}>Text colour</Typography>
            <ColorRow value={selected.color} onChange={(c) => onUpdate({ color: c })} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: PALETTE.body, mb: 0.5 }}>Background</Typography>
            <Stack direction="row" spacing={0.75} alignItems="center">
              <Box onClick={() => onUpdate({ bg: null })} sx={{ px: 1, height: 24, display: 'flex', alignItems: 'center', borderRadius: 1, fontSize: 10, fontWeight: 700, cursor: 'pointer', border: `2px solid ${!selected.bg ? PALETTE.primary : PALETTE.divider}` }}>None</Box>
              {['#000000', '#FFFFFF', '#3D5AFE', '#FF3B30'].map((c) => (
                <Box key={c} onClick={() => onUpdate({ bg: c })} sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: c, cursor: 'pointer', border: `2px solid ${selected.bg === c ? PALETTE.primary : PALETTE.divider}`, boxShadow: c === '#FFFFFF' ? 'inset 0 0 0 1px #ddd' : 'none' }} />
              ))}
            </Stack>
          </Box>
          <SliderRow label="Rotation" value={selected.rotation} min={-180} max={180} onChange={(_, v) => onUpdate({ rotation: v })}>
            <NumField value={selected.rotation} min={-180} max={180} suffix="°" width={40} onChange={(n) => onUpdate({ rotation: Math.min(180, Math.max(-180, Math.round(n))) })} />
          </SliderRow>
          <PositionControl onAlign={onAlign} />
          <DeleteButton onClick={onDelete}>Delete text</DeleteButton>
        </SelectedCard>
      )}
    </Box>
  );
}

// Compact numeric field with an optional unit suffix.
function NumField({ value, min, max, suffix, onChange, width = 36 }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <TextField
        size="small"
        type="number"
        value={value}
        onChange={(e) => { const n = Number(e.target.value); if (!Number.isNaN(n)) onChange(n); }}
        inputProps={{ min, max, style: { padding: '4px 6px', width, textAlign: 'right', fontSize: 12, fontWeight: 700 } }}
      />
      {suffix && <Typography sx={{ fontSize: 11, fontWeight: 700, color: PALETTE.muted }}>{suffix}</Typography>}
    </Box>
  );
}

// Slider row: label on the left, a typeable value (children) on the right. Shared everywhere.
function SliderRow({ label, children, ...sliderProps }) {
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.25 }}>
        <Typography sx={{ fontSize: 11, fontWeight: 700, color: PALETTE.body }}>{label}</Typography>
        {children}
      </Box>
      <Slider size="small" {...sliderProps} />
    </Box>
  );
}

function AnchorGrid({ onAlign }) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0.75 }}>
      {ANCHORS.map(([code, glyph]) => (
        <Box
          key={code}
          onClick={() => onAlign(code)}
          sx={{
            aspectRatio: '1 / 1', display: 'flex', alignItems: 'center', justifyContent: 'center',
            borderRadius: `${RADII.base}px`, cursor: 'pointer', fontSize: 16, transition: 'all .12s',
            border: `1px solid ${PALETTE.divider}`, color: PALETTE.muted, bgcolor: PALETTE.surface,
            '&:hover': { bgcolor: PALETTE.primary, borderColor: PALETTE.primary, color: '#fff', boxShadow: SHADOWS.card },
          }}
        >
          {glyph}
        </Box>
      ))}
    </Box>
  );
}

// Shared "Position" block — identical across Logo, Sticker and Text panels.
function PositionControl({ onAlign }) {
  return (
    <Box>
      <Typography sx={{ fontSize: 11, fontWeight: 700, color: PALETTE.body, mb: 0.6 }}>Position</Typography>
      <AnchorGrid onAlign={onAlign} />
    </Box>
  );
}

// Shared size + rotation controls (typeable) for image / emoji overlays.
function SizeRotationControls({ o, onUpdate }) {
  return (
    <>
      <SliderRow label="Size" value={o.size} min={0.05} max={1} step={0.01} onChange={(_, v) => onUpdate({ size: v })}>
        <NumField value={Math.round((o.size || 0) * 100)} min={3} max={100} suffix="%" onChange={(n) => onUpdate({ size: Math.min(1, Math.max(0.03, n / 100)) })} />
      </SliderRow>
      <SliderRow label="Rotation" value={o.rotation || 0} min={-180} max={180} onChange={(_, v) => onUpdate({ rotation: v })}>
        <NumField value={o.rotation || 0} min={-180} max={180} suffix="°" width={40} onChange={(n) => onUpdate({ rotation: Math.min(180, Math.max(-180, Math.round(n))) })} />
      </SliderRow>
    </>
  );
}

// Card that groups the controls for the currently-selected overlay.
function SelectedCard({ title, children }) {
  return (
    <Box sx={{ mt: 0.5, p: 1.5, borderRadius: `${RADII.card}px`, bgcolor: PALETTE.secondaryNav, border: `1px solid ${PALETTE.divider}` }}>
      <Typography sx={{ fontSize: 10, fontWeight: 800, color: PALETTE.muted, letterSpacing: '.08em', mb: 1.25 }}>{title}</Typography>
      <Stack spacing={1.5}>{children}</Stack>
    </Box>
  );
}

// Danger button with a soft hover, used for overlay delete actions.
function DeleteButton({ children, onClick }) {
  return (
    <Button fullWidth variant="outlined" color="error" size="small" startIcon={<DeleteOutlineRoundedIcon />} onClick={onClick} sx={{ textTransform: 'none', fontWeight: 700, borderColor: 'rgba(255,77,79,.4)', '&:hover': { bgcolor: 'rgba(255,77,79,.06)', borderColor: 'error.main' } }}>{children}</Button>
  );
}

function ImagePanel({ saved, selected, onUploadClick, onAddSaved, onRemoveSaved, onUpdate, onAlign, onDelete }) {
  return (
    <Box>
      <PanelTitle>Logo / Image</PanelTitle>
      <Button fullWidth variant="contained" disableElevation startIcon={<AddPhotoAlternateRoundedIcon />} onClick={onUploadClick} sx={{ textTransform: 'none', fontWeight: 800, mb: 1.5 }}>Upload image</Button>

      {saved.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
            <Typography sx={{ fontSize: 10, fontWeight: 800, color: PALETTE.muted, letterSpacing: '.08em' }}>SAVED IMAGES</Typography>
            <Typography sx={{ fontSize: 10, fontWeight: 700, color: PALETTE.muted }}>{saved.length}</Typography>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0.75 }}>
            {saved.map((s) => (
              <Box key={s.id} title="Click to add" sx={{ position: 'relative', pt: '100%', borderRadius: `${RADII.base}px`, overflow: 'hidden', border: `1px solid ${PALETTE.divider}`, cursor: 'pointer', transition: 'border-color .12s, box-shadow .12s', '&:hover': { borderColor: PALETTE.primary, boxShadow: SHADOWS.card }, '&:hover .rm': { opacity: 1 } }} onClick={() => onAddSaved(s)}>
                <Box component="img" src={s.src} alt="saved" sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain', bgcolor: PALETTE.inputFill }} />
                <Box className="rm" onClick={(e) => { e.stopPropagation(); onRemoveSaved(s.id); }} sx={{ position: 'absolute', top: 3, right: 3, width: 17, height: 17, borderRadius: '50%', bgcolor: 'rgba(0,0,0,.62)', color: '#fff', fontSize: 12, lineHeight: '17px', textAlign: 'center', opacity: 0, transition: 'opacity .15s', '&:hover': { bgcolor: PALETTE.error } }}>×</Box>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {selected ? (
        <SelectedCard title="SELECTED IMAGE">
          <SizeRotationControls o={selected} onUpdate={onUpdate} />
          <PositionControl onAlign={onAlign} />
          <DeleteButton onClick={onDelete}>Delete image</DeleteButton>
        </SelectedCard>
      ) : (
        <Typography sx={{ fontSize: 11, color: PALETTE.muted }}>Upload a logo or image, then select it on the canvas to resize, rotate and snap it to a corner or edge.</Typography>
      )}
    </Box>
  );
}

function StickerPanel({ selected, onAddClick, onUpdate, onAlign, onDelete }) {
  return (
    <Box>
      <PanelTitle>Sticker</PanelTitle>
      <Button fullWidth variant="contained" disableElevation startIcon={<EmojiEmotionsRoundedIcon />} onClick={onAddClick} sx={{ textTransform: 'none', fontWeight: 800, mb: selected ? 2 : 1 }}>Add emoji</Button>
      {selected ? (
        <SelectedCard title="SELECTED STICKER">
          <SizeRotationControls o={selected} onUpdate={onUpdate} />
          <PositionControl onAlign={onAlign} />
          <DeleteButton onClick={onDelete}>Delete sticker</DeleteButton>
        </SelectedCard>
      ) : (
        <Typography sx={{ fontSize: 11, color: PALETTE.muted }}>Add an emoji, then select it on the canvas to resize, rotate and snap it into position.</Typography>
      )}
    </Box>
  );
}

function ResizePanel({ resize, baked, setResize }) {
  const setW = (v) => {
    const w = Number(v);
    if (resize.lock && baked) setResize({ ...resize, width: w, height: Math.round(w * (baked.h / baked.w)) });
    else setResize({ ...resize, width: w });
  };
  const setH = (v) => {
    const h = Number(v);
    if (resize.lock && baked) setResize({ ...resize, height: h, width: Math.round(h * (baked.w / baked.h)) });
    else setResize({ ...resize, height: h });
  };
  return (
    <Box>
      <PanelTitle>Resize output</PanelTitle>
      <ToggleButton value="on" selected={resize.enabled} onChange={() => setResize({ ...resize, enabled: !resize.enabled, width: resize.width || baked?.w || 0, height: resize.height || baked?.h || 0 })} size="small" fullWidth sx={{ textTransform: 'none', fontWeight: 700, mb: 1.5 }}>
        {resize.enabled ? 'Custom size ON' : 'Use original size'}
      </ToggleButton>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ opacity: resize.enabled ? 1 : 0.5, pointerEvents: resize.enabled ? 'auto' : 'none' }}>
        <TextField size="small" type="number" label="W" value={resize.width || ''} onChange={(e) => setW(e.target.value)} sx={{ width: 90 }} />
        <Typography sx={{ color: PALETTE.muted }}>×</Typography>
        <TextField size="small" type="number" label="H" value={resize.height || ''} onChange={(e) => setH(e.target.value)} sx={{ width: 90 }} />
      </Stack>
      <ToggleButton value="lock" selected={resize.lock} onChange={() => setResize({ ...resize, lock: !resize.lock })} size="small" sx={{ textTransform: 'none', fontWeight: 700, mt: 1 }}>
        {resize.lock ? 'Aspect locked' : 'Aspect free'}
      </ToggleButton>
    </Box>
  );
}

function StrokeSvg({ o }) {
  const pts = (o.points || []).map((p) => `${p.x * 100},${p.y * 100}`).join(' ');
  const sw = (o.stroke || 0.008) * 100;
  const common = { stroke: o.color, strokeWidth: sw, fill: 'none', vectorEffect: 'non-scaling-stroke', strokeLinecap: 'round', strokeLinejoin: 'round' };
  if (o.shape === 'rect' && o.points?.length >= 2) {
    const [a, b] = [o.points[0], o.points[o.points.length - 1]];
    return <rect x={Math.min(a.x, b.x) * 100} y={Math.min(a.y, b.y) * 100} width={Math.abs(b.x - a.x) * 100} height={Math.abs(b.y - a.y) * 100} {...common} strokeWidth={sw * 3} />;
  }
  if (o.shape === 'ellipse' && o.points?.length >= 2) {
    const [a, b] = [o.points[0], o.points[o.points.length - 1]];
    return <ellipse cx={(a.x + b.x) / 2 * 100} cy={(a.y + b.y) / 2 * 100} rx={Math.abs(b.x - a.x) / 2 * 100} ry={Math.abs(b.y - a.y) / 2 * 100} {...common} strokeWidth={sw * 3} />;
  }
  return <polyline points={pts} {...common} strokeWidth={sw * 3} />;
}

// A positioned overlay (text / sticker / redact) with move + resize handles.
function OverlayEl({ o, disp, selected, onDragStart, onChangeText }) {
  const left = o.x * disp.w;
  const top = o.y * disp.h;
  if (o.type === 'redact') {
    const w = o.w * disp.w; const h = o.h * disp.h;
    return (
      <Box
        onPointerDown={(e) => onDragStart(e, o, 'move')}
        sx={{
          position: 'absolute', left: o.x * disp.w, top: o.y * disp.h, width: w, height: h, cursor: 'move',
          border: selected ? '2px solid #3D5AFE' : '1px dashed rgba(255,255,255,.7)',
          backdropFilter: o.mode === 'blur' ? 'blur(6px)' : 'none',
          bgcolor: o.mode === 'block' ? '#000' : o.mode === 'pixelate' ? 'rgba(0,0,0,.35)' : 'rgba(255,255,255,.05)',
        }}
      >
        {o.mode === 'pixelate' && <Box sx={{ position: 'absolute', inset: 0, backgroundImage: 'repeating-conic-gradient(rgba(0,0,0,.4) 0% 25%, rgba(255,255,255,.15) 0% 50%)', backgroundSize: '10px 10px' }} />}
        {selected && <ResizeHandle onPointerDown={(e) => onDragStart(e, o, 'size')} />}
      </Box>
    );
  }
  if (o.type === 'sticker') {
    const size = o.size * Math.min(disp.w, disp.h);
    return (
      <Box onPointerDown={(e) => onDragStart(e, o, 'move')} sx={{ position: 'absolute', left, top, transform: `translate(-50%,-50%) rotate(${o.rotation || 0}deg)`, cursor: 'move', lineHeight: 1, outline: selected ? '2px solid #3D5AFE' : 'none' }}>
        {o.src
          ? <Box component="img" src={o.src} alt="overlay" draggable={false} sx={{ width: size, height: 'auto', display: 'block', userSelect: 'none' }} />
          : <Box sx={{ fontSize: size, userSelect: 'none' }}>{o.char}</Box>}
        {selected && <ResizeHandle onPointerDown={(e) => onDragStart(e, o, 'size')} />}
      </Box>
    );
  }
  // text
  const fontPx = o.fontSize * disp.h;
  return (
    <Box
      onPointerDown={(e) => onDragStart(e, o, 'move')}
      onDoubleClick={(e) => { e.stopPropagation(); const t = window.prompt('Edit text', o.text); if (t != null) onChangeText(t); }}
      sx={{ position: 'absolute', left, top, transform: `translate(-50%,-50%) rotate(${o.rotation || 0}deg)`, cursor: 'move', outline: selected ? '2px solid #3D5AFE' : 'none', p: 0.25, maxWidth: disp.w }}
    >
      <Box sx={{ fontFamily: o.fontFamily, fontSize: fontPx, fontWeight: o.weight, color: o.color, textAlign: o.align, whiteSpace: 'pre-wrap', lineHeight: 1.18, userSelect: 'none', px: o.bg ? fontPx * 0.28 + 'px' : 0, py: o.bg ? fontPx * 0.12 + 'px' : 0, bgcolor: o.bg || 'transparent', borderRadius: o.bg ? fontPx * 0.22 + 'px' : 0, display: 'inline-block' }}>
        {o.text || ' '}
      </Box>
      {selected && <ResizeHandle onPointerDown={(e) => onDragStart(e, o, 'size')} />}
    </Box>
  );
}

function ResizeHandle({ onPointerDown }) {
  return <Box onPointerDown={onPointerDown} sx={{ position: 'absolute', right: -7, bottom: -7, width: 14, height: 14, borderRadius: '50%', bgcolor: '#fff', border: '2px solid #3D5AFE', cursor: 'nwse-resize' }} />;
}
